import React, { useEffect, useContext, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_RowSelectionState,
} from "material-react-table";
import { EditIcon } from "../../assets/Icons";
import DeleteIcon from "@mui/icons-material/Delete";
import { CSDataTableColumns } from "../../modal/CSDataTable";
import { IconButton, Tooltip } from "@mui/material";
import { RenameFolderIcon } from "../../assets/FileRepositoryIcon";
import { DeleteFolderIcon } from "../../assets/FileRepositoryIcon";
import EditOutlined from "@mui/icons-material/EditOutlined";

const CSDataTable = (props: {
  rows: any;
  columns: CSDataTableColumns[];
  previewRecord?: any;
  handleSelectPreview?: (e: any) => void;
  multiRowSelection?: boolean;
  handleSelectRow?: (e: any) => void;
  handleEdit?: (row: any) => void;
  handleDelete?: (row: any) => void;
  enableRowSelection?: boolean;
  enableColumnFilter?: boolean;
}) => {
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [previewRecord, setPreviewRecord] = useState<string>("");
  const [columns, setColumns] = useState<any[]>([]);

  useEffect(() => {
    if (props.previewRecord && props.previewRecord.rowId) {
      const selectedRowId = props.previewRecord.rowId.toString();
      setPreviewRecord(selectedRowId);
    }
  }, [props.previewRecord]);

  useEffect(() => {
    if (previewRecord !== "" && props.handleSelectPreview) {
      const selectedRecord = props.rows.find(
        (ele: any) => previewRecord === ele.rowId + ""
      );
      props.handleSelectPreview(selectedRecord);
    }
  }, [previewRecord]);

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection);

    if (selectedIds.length > 0) {
      const selectedRows = props.rows.filter((ele: any) =>
        selectedIds.includes(ele.rowId + "")
      );

      props && props.handleSelectRow && props.handleSelectRow(selectedRows);
    } else {
      props && props.handleSelectRow && props.handleSelectRow([]);
    }
  }, [rowSelection]);

  const getCellContent = (row: any, column: CSDataTableColumns) => {
    const { accessorKey, size, isDeletable, isEditable, disableDeleteCondition } = column;

    switch (accessorKey) {
      case "edit":
        return props.handleEdit ? (
          <IconButton sx={{
            cursor: !isEditable ? 'default' : 'pointer',
          }} disabled={!isEditable} onClick={() => props.handleEdit!(row)}>
            {React.createElement(EditIcon, {
              opacity: (!isEditable) ? "50%" : "100%",
            })}
          </IconButton>
        ) : null;
      case "delete":
        const isDeleteDisabled = !isDeletable || (isDeletable && disableDeleteCondition ? disableDeleteCondition(row) : false); console.log("isDeleteDisabled", isDeleteDisabled, disableDeleteCondition);
        return props.handleDelete ? (
          <IconButton sx={{
            cursor: isDeleteDisabled ? 'default' : 'pointer',
          }} disabled={isDeleteDisabled} onClick={() => props.handleDelete!(row)}>
            {React.createElement(DeleteFolderIcon, {
              opacity: (isDeleteDisabled) ? "50%" : "100%",
            })}
          </IconButton>
        ) : null;
      default:
        return column.tooltip ? (
          <Tooltip title={row[accessorKey]}>
            <div
              style={{
                width: size,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {row[accessorKey]}
            </div>
          </Tooltip>
        ) : (
          row[accessorKey]
        );
    }
  };

  useEffect(() => {
    const modColumn = props.columns.map((column) => ({
      ...column,
      Cell: ({ row }: any) => getCellContent(row.original, column),
    }));

    setColumns(modColumn);
  }, [props.columns]);

  const table = useMaterialReactTable({
    data: props.rows,
    columns: columns,
    initialState: { density: "compact", showColumnFilters: props.enableColumnFilter },
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        height: '100%', 
        maxHeight: '80vh', 
        overflow: 'auto',
      },
    },
    muiTableBodyCellProps: { sx: { border: "none", boxShadow: 0 } },
    muiTablePaperProps: { sx: { boxShadow: 0 } },
    muiBottomToolbarProps: { sx: { display: "none" } },
    enableDensityToggle: false,
    enableTopToolbar: false,
    enablePagination: false,
    enableColumnActions: false,
    enableRowSelection: props.enableRowSelection !== false,
    getRowId: (row) => row.rowId + "",
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
    enableMultiRowSelection: props.multiRowSelection,
    enableSorting: false,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => setPreviewRecord(row.id),
      sx: {
        backgroundColor: row.id == previewRecord ? "#cce1ff" : "transparent",
        cursor: "pointer",
        "&.Mui-selected": {
          backgroundColor: row.id == previewRecord ? "#cce1ff" : "transparent",
        },
        "&.Mui-selected:hover": {
          backgroundColor: row.id == previewRecord ? "#cce1ff" : "transparent",
        },
        "&.Mui-selected td:after": {
          backgroundColor: row.id == previewRecord ? "#cce1ff" : "transparent",
        },
      },
    }),
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        color: "#292D32",
        opacity: "40%",
        fontStyle: "italic",
        fontWeight: 600,
      },
    }),
  });

  return <MaterialReactTable table={table} />;
};

export default CSDataTable;
